<script lang="ts">
  import { Farmacia, type Medicamento, type Lote, type StockMedicamento } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import SearchSelect from './SearchSelect.svelte';
  import HelpHint from './HelpHint.svelte';
  import { session } from '../lib/auth.svelte';
  import { parseSpreadsheet, csvCell } from '../lib/csv-import';

  let meds = $state<Medicamento[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  let nuevoMed = $state<Medicamento>({ codigo: '', nombre: '', presentacion: '', principioActivo: '', requiereReceta: true, esControlado: false });
  let nuevoLote = $state<Lote>({ medicamentoId: '', bodega: 'FAR-HOSP', numeroLote: '', vencimiento: '', cantidad: 0 });
  let disp = $state<{ medicamentoId: string; pacienteId: string; cantidad: number }>({ medicamentoId: '', pacienteId: '', cantidad: 1 });

  let stockMedId = $state('');
  let stockData = $state<StockMedicamento | null>(null);
  let vencidos = $state<Lote[]>([]);
  let proximos = $state<Lote[]>([]);
  let medQ = $state('');

  const medsVisibles = $derived.by(() => {
    const q = medQ.trim().toLowerCase();
    const list = q ? meds.filter(m => `${m.codigo} ${m.nombre} ${m.principioActivo ?? ''}`.toLowerCase().includes(q)) : meds;
    return list.slice(0, 10);
  });
  const medItems = $derived(meds.map(m => ({ id: m.id!, label: `${m.codigo} · ${m.nombre}` })));

  const nombreDe = (id?: string) => meds.find(m => m.id === id)?.nombre ?? (id ? id.slice(0, 8) : '—');
  const codigoDe = (id?: string) => meds.find(m => m.id === id)?.codigo ?? '';

  function fmtFecha(iso?: string) {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }

  function diasRestantes(iso: string) {
    const v = new Date(iso + 'T00:00:00');
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    return Math.round((v.getTime() - hoy.getTime()) / 86400000);
  }

  async function cargar() {
    loading = true; error = null;
    try {
      meds = await Farmacia.meds();
      const [v, p] = await Promise.all([Farmacia.vencidos(), Farmacia.proximosVencer(30)]);
      vencidos = v; proximos = p;
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function importarMeds(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    error = null; info = null;
    try {
      const { rows } = await parseSpreadsheet(file);
      let creados = 0;
      for (const row of rows) {
        const codigo = csvCell(row, 'codigo', 'code');
        const nombre = csvCell(row, 'nombre', 'name', 'medicamento');
        if (!codigo || !nombre) continue;
        await Farmacia.crearMed({
          codigo, nombre,
          principioActivo: csvCell(row, 'principioactivo', 'principio', 'activo') || '',
          presentacion: csvCell(row, 'presentacion', 'forma') || '',
          requiereReceta: true, esControlado: false,
        });
        creados++;
      }
      info = `Importación: ${creados} medicamentos añadidos.`;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { input.value = ''; }
  }

  async function cargarStock() {
    if (!stockMedId) { stockData = null; return; }
    try { stockData = await Farmacia.stock(stockMedId); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearMed(e: SubmitEvent) {
    e.preventDefault();
    error = null; info = null;
    try {
      await Farmacia.crearMed({ ...nuevoMed });
      nuevoMed = { codigo: '', nombre: '', presentacion: '', principioActivo: '', requiereReceta: true, esControlado: false };
      info = 'Medicamento creado.';
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearLote(e: SubmitEvent) {
    e.preventDefault();
    error = null; info = null;
    if (!nuevoLote.medicamentoId) { error = 'Selecciona medicamento.'; return; }
    try {
      await Farmacia.crearLote({ ...nuevoLote, cantidad: Number(nuevoLote.cantidad) });
      nuevoLote = { ...nuevoLote, numeroLote: '', vencimiento: '', cantidad: 0 };
      info = 'Lote creado.';
      await cargar();
      if (stockMedId === nuevoLote.medicamentoId) await cargarStock();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function dispensar(e: SubmitEvent) {
    e.preventDefault();
    error = null; info = null;
    if (!disp.medicamentoId || !disp.pacienteId) { error = 'Medicamento y paciente requeridos.'; return; }
    try {
      const r = await Farmacia.dispensar({
        medicamentoId: disp.medicamentoId,
        pacienteId: disp.pacienteId,
        cantidad: Number(disp.cantidad),
        dispensadoPor: session.value?.usuario ?? 'sistema'
      });
      info = `Dispensadas ${r.dispensado} unidades (FEFO).`;
      disp = { ...disp, cantidad: 1 };
      if (stockMedId === disp.medicamentoId) await cargarStock();
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
  $effect(() => { cargarStock(); });
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Farmacia hospitalaria</h2>
      <p class="muted">Medicamentos, stock, lotes, dispensación FEFO y vencimientos</p>
      <HelpHint title="Ayuda · Farmacia">
        <p>Use el buscador (máx. 10 resultados). Para carga masiva <strong>suba un Excel o CSV</strong> con columnas codigo, nombre, presentacion. Al cumplir el kárdex se descuenta stock automáticamente.</p>
      </HelpHint>
    </div>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  {#if vencidos.length > 0 || proximos.length > 0}
    <div class="panel alerta">
      <h3>⚠ Alertas de vencimiento</h3>
      {#if vencidos.length > 0}
        <p class="sub rojo">Vencidos (no dispensables)</p>
        <table>
          <thead><tr><th>Medicamento</th><th>Lote</th><th>Bodega</th><th>Vence</th><th class="num">Stock</th></tr></thead>
          <tbody>
            {#each vencidos as l (l.id)}
              <tr>
                <td><strong>{nombreDe(l.medicamentoId)}</strong><span class="muted sm"> {codigoDe(l.medicamentoId)}</span></td>
                <td>{l.numeroLote}</td>
                <td>{l.bodega ?? '—'}</td>
                <td class="rojo">{fmtFecha(l.vencimiento)}</td>
                <td class="num">{l.cantidad}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
      {#if proximos.length > 0}
        <p class="sub naranja">Próximos a vencer (30 días)</p>
        <table>
          <thead><tr><th>Medicamento</th><th>Lote</th><th>Bodega</th><th>Vence</th><th class="num">Stock</th></tr></thead>
          <tbody>
            {#each proximos as l (l.id)}
              <tr>
                <td><strong>{nombreDe(l.medicamentoId)}</strong><span class="muted sm"> {codigoDe(l.medicamentoId)}</span></td>
                <td>{l.numeroLote}</td>
                <td>{l.bodega ?? '—'}</td>
                <td class="naranja">{fmtFecha(l.vencimiento)} ({diasRestantes(l.vencimiento)} d)</td>
                <td class="num">{l.cantidad}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  {/if}

  <div class="panel">
    <h3>Medicamentos</h3>
    <form onsubmit={crearMed} class="row">
      <label class="field"><span>Código</span><input placeholder="Código" bind:value={nuevoMed.codigo} required /></label>
      <label class="field"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevoMed.nombre} required /></label>
      <label class="field"><span>Principio activo</span><input placeholder="Principio activo" bind:value={nuevoMed.principioActivo} /></label>
      <label class="field"><span>Presentación</span><input placeholder="Presentación" bind:value={nuevoMed.presentacion} /></label>
      <label class="chk"><input type="checkbox" bind:checked={nuevoMed.requiereReceta} /> Receta</label>
      <label class="chk"><input type="checkbox" bind:checked={nuevoMed.esControlado} /> Controlado</label>
      <button type="submit">+ Medicamento</button>
    </form>
    <div class="row" style="margin-top:.5rem">
      <label class="field grow"><span>Buscar (máx. 10)</span>
        <input type="search" placeholder="Nombre o código…" bind:value={medQ} />
      </label>
      <label class="field"><span>Subir Excel / CSV</span>
        <input type="file" accept=".csv,.xlsx,.xls" onchange={importarMeds} />
      </label>
    </div>
    <table>
      <thead><tr><th>Código</th><th>Nombre</th><th>Principio activo</th><th>Presentación</th><th>Tipo</th></tr></thead>
      <tbody>
        {#each medsVisibles as m (m.id)}
          <tr>
            <td>{m.codigo}</td><td>{m.nombre}</td><td>{m.principioActivo ?? '—'}</td><td>{m.presentacion ?? '—'}</td>
            <td>
              {#if m.requiereReceta}<span class="tag">Rx</span>{/if}
              {#if m.esControlado}<span class="tag ctrl">Controlado</span>{/if}
            </td>
          </tr>
        {:else}<tr><td colspan="5" class="empty">Sin medicamentos.</td></tr>{/each}
      </tbody>
    </table>
  </div>

  <div class="panel">
    <h3>Stock</h3>
    <div class="row">
      <div style="min-width:260px;flex:1">
        <SearchSelect items={medItems} bind:value={stockMedId} placeholder="Buscar medicamento…" />
      </div>
    </div>
    {#if stockData}
      <div class="stock-resumen">
        <div class="total"><span class="k">Total</span><strong>{stockData.total}</strong></div>
        {#each Object.entries(stockData.porBodega) as [bodega, cant]}
          <div class="bodega"><span class="k">{bodega}</span><strong>{cant}</strong></div>
        {/each}
      </div>
      {#if stockData.lotes.length > 0}
        <table class="mt">
          <thead><tr><th>Lote</th><th>Bodega</th><th>Vence</th><th class="num">Stock</th></tr></thead>
          <tbody>
            {#each stockData.lotes as l (l.id)}
              <tr><td>{l.numeroLote}</td><td>{l.bodega ?? '—'}</td><td>{fmtFecha(l.vencimiento)}</td><td class="num">{l.cantidad}</td></tr>
            {/each}
          </tbody>
        </table>
      {/if}
    {:else if stockMedId}
      <p class="muted">Sin stock disponible.</p>
    {/if}
  </div>

  <div class="panel">
    <h3>Ingresar lote</h3>
    <form onsubmit={crearLote} class="form-grid">
      <SearchSelect items={medItems} bind:value={nuevoLote.medicamentoId} placeholder="Buscar medicamento…" required />
      <input placeholder="Bodega" bind:value={nuevoLote.bodega} />
      <input placeholder="N° Lote" bind:value={nuevoLote.numeroLote} required />
      <input type="date" bind:value={nuevoLote.vencimiento} required />
      <input type="number" min="1" placeholder="Cantidad" bind:value={nuevoLote.cantidad} required />
      <button type="submit" class="full">+ Lote</button>
    </form>
  </div>

  <div class="panel">
    <h3>Dispensar (FEFO)</h3>
    <form onsubmit={dispensar} class="form-grid">
      <SearchSelect items={medItems} bind:value={disp.medicamentoId} placeholder="Buscar medicamento…" required />
      <PacientePicker bind:value={disp.pacienteId} onPick={(id) => disp.pacienteId = id} />
      <input type="number" min="1" bind:value={disp.cantidad} required />
      <button type="submit" class="full">Dispensar</button>
    </form>
  </div>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  .sm { font-size: 0.75rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); margin: 0 0 1rem; }
  .panel h3 { margin: 0 0 .75rem; font-size: 1rem; color: #166534; }
  .panel.alerta { border-color: #fecaca; background: #fff7f7; }
  .panel.alerta h3 { color: #b91c1c; }
  .sub { font-size: .78rem; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; margin: .75rem 0 .25rem; }
  .rojo { color: #dc2626; }
  .naranja { color: #d97706; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .row input, .row button, .form-grid input, .form-grid button, .form-grid > :global(.picker) {
    padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827;
  }
  .row input:focus, .form-grid input:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .row button, .form-grid button { border: 0; }
  .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .5rem; align-items: end; }
  .full { grid-column: span 3; }
  .chk { font-size: .85rem; display: flex; align-items: center; gap: .25rem; color: #374151; }
  table { width: 100%; border-collapse: collapse; }
  .mt { margin-top: .5rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .5rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .9rem; color: #111827; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .tag { background: #dcfce7; color: #166534; padding: .1rem .45rem; border-radius: 6px; font-size: .72rem; margin-right: .25rem; }
  .tag.ctrl { background: #fee2e2; color: #dc2626; }
  .empty { text-align: center; color: #9ca3af; font-style: italic; }
  .err { color: #dc2626; }
  .ok { color: #166534; background: #dcfce7; padding: .35rem .55rem; border-radius: 8px; }
  .stock-resumen { display: flex; gap: .75rem; flex-wrap: wrap; margin: .5rem 0; }
  .stock-resumen > div { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: .6rem 1rem; text-align: center; min-width: 90px; }
  .stock-resumen .k { display: block; font-size: .68rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; }
  .stock-resumen strong { font-size: 1.4rem; color: #166534; }
</style>
